import { useEffect, useState, useCallback } from "react";
import {
    Box,
    Modal,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    IconButton,
    Pagination,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dashboardApi from "../../services/dashboard";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    Search,
    SearchIconWrapper,
    StyledInputBase,
    StyledTableCell,
    StyledTableRow,
} from "../../styles/styles";
import { BsSearch } from "react-icons/bs";
import TableLoader from "../TableLoader";
import { FormateDate } from "../../utils";

const feedbackTypeLabel = (type) => {
    switch (type) {
        case "P":
            return "Parent";
        case "L":
            return "Lasmab Staff";
        case "S":
            return "Student";
        default:
            return "Unknown";
    }
};

export default function FeedbackModal() {
    const [viewOpen, setViewOpen] = useState(false);
    const [replyOpen, setReplyOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [data, setData] = useState([]);
    const [responseData, setResponseData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingResponse, setLoadingResponse] = useState(false);
    const [searchText, setSearchText] = useState("");
    const { user, admToken } = useSelector((state) => state.auth);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 0,
    });

    const getFeedbacks = useCallback(async () => {
        try {
            setLoading(true);
            const res = await dashboardApi.getFeedbacks(
                admToken,
                searchText,
                pagination.pageSize,
                pagination.pageNumber,
                user.schoolId
            );
            const response = res.data;
            if (response.status && response.statusCode === 2) {
                setData(response.data);
                setPagination((prev) => ({
                    ...prev,
                    totalRecords: response.totalRecord,
                    totalPages: Math.ceil(response.totalRecord / pagination.pageSize),
                    currentPage: response.currentPage || 1,
                }));
            } else {
                toast.error(response.data.message || "Failed to fetch feedbacks");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred while fetching feedbacks");
        } finally {
            setLoading(false);
        }
    }, [admToken, searchText, pagination.pageSize, pagination.pageNumber, user.schoolId]);

    const handleGetResponse = async (ticketId) => {
        if (!ticketId) return;
        try {
            setLoadingResponse(true);
            const res = await dashboardApi.getFeedbacksResponse(admToken, ticketId);
            const response = res.data;
            if (response.status && response.statusCode === 2) {
                setResponseData(response.data);
            } else {
                toast.error(response.data.message || "Failed to fetch feedbacks");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred while fetching feedbacks");
        } finally {
            setLoadingResponse(false);
        }
    };

    useEffect(() => {
        getFeedbacks();
    }, [pagination.pageNumber, searchText]);

    const handleView = (row) => {
        setSelectedRow(row);
        setViewOpen(true);
        handleGetResponse(row.ticketId);
    };

    const handleReply = (row) => {
        setSelectedRow(row);
        setReplyOpen(true);
    };

    const handleCloseView = () => setViewOpen(false);
    const handleCloseReply = () => setReplyOpen(false);

    const handlePageChange = (event, newPage) => {
        setPagination((prev) => ({ ...prev, pageNumber: newPage }));
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
        getFeedbacks();
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        getFeedbacks();
    };

    return (
        <Box>
            <Box
                sx={{
                    pt: 4,
                    border: "1px solid #671E78",
                    borderRadius: "20px",
                }}
            >
                <Box component="form" onSubmit={handleSearchSubmit} sx={{ width: "100%" }}>
                    <Search
                        sx={{
                            width: { lg: "345px", md: "330px", sm: "auto", xs: "auto" },
                            pl: { xs: 6 },
                        }}
                    >
                        <SearchIconWrapper>
                            <BsSearch />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Search..."
                            inputProps={{ "aria-label": "search" }}
                            value={searchText}
                            onChange={handleSearchChange}
                        />
                    </Search>
                </Box>

                <TableContainer component={Paper} sx={{ pt: 3 }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#f4effa" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>S/N</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Subject</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>User Type</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
                                <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <StyledTableRow>
                                    <StyledTableCell
                                        colSpan={6}
                                        sx={{
                                            textAlign: "center",
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            py: 2,
                                        }}
                                    >
                                        <TableLoader />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : data?.length > 0 ? (
                                data.map((row, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>{row.subject}</TableCell>
                                        <TableCell>{feedbackTypeLabel(row.feedbackType)}</TableCell>
                                        <TableCell>{row.message?.slice(0, 25)}...</TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handleView(row)}
                                                    sx={{
                                                        backgroundColor: "#671E78",
                                                        color: "#fff",
                                                        textTransform: "none",
                                                        fontFamily: "Gilroy-Regular",
                                                        fontSize: "0.75rem",
                                                        py: 0.4,
                                                        px: 1.5,
                                                        "&:hover": { backgroundColor: "#54125f" },
                                                    }}
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleReply(row)}
                                                    sx={{
                                                        borderColor: "#671E78",
                                                        color: "#671E78",
                                                        textTransform: "none",
                                                        fontFamily: "Gilroy-Regular",
                                                        fontSize: "0.75rem",
                                                        py: 0.4,
                                                        px: 1.5,
                                                        "&:hover": {
                                                            borderColor: "#54125f",
                                                            backgroundColor: "rgba(103, 30, 120, 0.05)",
                                                        },
                                                    }}
                                                >
                                                    Reply
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={7} align="center">
                                        No feedback found
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {pagination.totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "end", mt: 3, mb: 2 }}>
                        <Pagination
                            count={pagination.totalPages}
                            page={pagination.currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            sx={{
                                "& .MuiPaginationItem-root": { color: "#671E78" },
                                "& .Mui-selected": {
                                    backgroundColor: "rgba(103, 30, 120, 0.1)",
                                },
                            }}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </Box>

            {/* View Modal */}
            <Modal open={viewOpen} onClose={handleCloseView}
                sx={{ zIndex: 1450 }}
                disablePortal={false}
                keepMounted

            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 500,
                        bgcolor: "#fff",
                        borderRadius: "12px",
                        boxShadow: 24,
                        p: 4,
                        outline: "none",
                        maxHeight: "80vh",
                        overflowY: "auto",
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                            View Feedback
                        </Typography>
                        <IconButton onClick={handleCloseView}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {loadingResponse && (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    )}
                    {!loadingResponse && responseData && (
                        <div className="p-5">
                            <p className="text-xl font-bold">{responseData.subject}</p>
                            <div className="my-5 text-sm leading-relaxed tracking-wide">
                                {responseData.message}
                            </div>
                            {responseData?.attachMentPath && (
                                <img
                                    src={responseData.attachMentPath}
                                    alt={responseData.subject}
                                    className="rounded-lg"
                                />
                            )}
                            {responseData?.feedbackResponses?.map((response, index) => (
                                <div
                                    className="ml-10 my-5 bg-gray-200 p-5 rounded-lg"
                                    key={index}
                                >
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm mt-3">
                                            {FormateDate(response.repliedDateTime)}
                                        </p>
                                        <p className="text-sm mt-3">{response.repliedByStaff}</p>
                                    </div>
                                    <div className="my-5 text-sm text-gray-600 leading-relaxed tracking-wide">
                                        {response.message}
                                        <p className="text-sm flex items-baseline justify-end">
                                            {response.feedbackId ? "Replied" : ""}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Box>
            </Modal>

            {/* Reply Modal */}
            <Modal open={replyOpen} onClose={handleCloseReply}
                sx={{ zIndex: 1450 }}
                disablePortal={false}
                keepMounted

            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 500,
                        bgcolor: "#fff",
                        borderRadius: "12px",
                        boxShadow: 24,
                        p: 4,
                        outline: "none",
                        maxHeight: "80vh",
                        overflowY: "auto",
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            Reply to Feedback
                        </Typography>
                        <IconButton onClick={handleCloseReply}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            const formData = new FormData(e.currentTarget);
                            const responseMessage = formData.get("message");

                            if (!responseMessage) {
                                toast.error("Message cannot be empty");
                                return;
                            }

                            const payload = new FormData();
                            payload.append("StaffId", user.staffId || user.id);
                            payload.append("ResponsMessage", responseMessage);
                            payload.append("TicketId", selectedRow?.ticketId);

                            try {
                                const res = await dashboardApi.replyFeedback(admToken, payload);
                                if (res.status === true && res.statusCode === 2) {
                                    toast.success(res.message || "Reply sent successfully!");
                                    handleCloseReply();
                                    getFeedbacks();
                                } else {
                                    toast.error(res?.message || "Failed to send reply");
                                }
                            } catch (error) {
                                console.error(error);
                                toast.error("Something went wrong!");
                            }
                        }}
                        sx={{ mt: 2, display: "grid", gap: 2 }}
                    >
                        <TextField
                            label="Subject"
                            fullWidth
                            disabled
                            value={selectedRow?.subject || ""}
                        />
                        <TextField
                            name="message"
                            label="Message"
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Type your reply..."
                        />
                        <Button
                            variant="contained"
                            sx={{ backgroundColor: "#6a1b9a" }}
                            type="submit"
                        >
                            Send Reply
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
